/**
 * Monetization abstraction — real interfaces, no fake ads.
 *
 * Providers stay INERT until real credentials are supplied via build-time env
 * (Vite `import.meta.env`). With no keys configured, ad calls resolve to
 * `{ status: 'not_configured' }` and purchases to `{ status: 'unavailable' }`.
 * Nothing fake is ever rendered or reported as watched/purchased.
 *
 * To go live, provide (per game, at build time) any of:
 *   VITE_ADS_PROVIDER = 'admob' | 'adsense' | 'h5'
 *   VITE_ADS_CLIENT              (AdSense publisher / AdMob app id)
 *   VITE_ADS_REWARDED_UNIT       (rewarded ad unit id)
 *   VITE_ADS_INTERSTITIAL_UNIT   (interstitial ad unit id)
 *   VITE_IAP_PROVIDER  = 'stripe' | 'play' | 'appstore'
 *   VITE_IAP_PUBLIC_KEY
 * A native wrapper (Capacitor) can register a real AdProvider/IAPProvider at
 * runtime via `monetization.useAdProvider()` / `useIapProvider()`.
 */

export type AdResult =
  | { status: 'watched' }
  | { status: 'dismissed' }
  | { status: 'not_configured' }
  | { status: 'error'; message: string };

export type PurchaseResult =
  | { status: 'purchased'; productId: string; receipt?: string }
  | { status: 'cancelled' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

export interface Product {
  id: string;
  title: string;
  description: string;
  priceLabel: string;
  /** grant applied client-side after a verified purchase */
  grant?: Record<string, number>;
}

export interface AdProvider {
  readonly id: string;
  isReady(): boolean;
  showRewarded(): Promise<AdResult>;
  showInterstitial(): Promise<AdResult>;
}

export interface IapProvider {
  readonly id: string;
  isReady(): boolean;
  getProducts(): Promise<Product[]>;
  purchase(productId: string): Promise<PurchaseResult>;
  restore(): Promise<string[]>;
}

interface Env {
  adsProvider?: string;
  adsClient?: string;
  rewardedUnit?: string;
  interstitialUnit?: string;
  iapProvider?: string;
  iapPublicKey?: string;
}

function readEnv(): Env {
  const e: any = (typeof import.meta !== 'undefined' && (import.meta as any).env) || {};
  return {
    adsProvider: e.VITE_ADS_PROVIDER,
    adsClient: e.VITE_ADS_CLIENT,
    rewardedUnit: e.VITE_ADS_REWARDED_UNIT,
    interstitialUnit: e.VITE_ADS_INTERSTITIAL_UNIT,
    iapProvider: e.VITE_IAP_PROVIDER,
    iapPublicKey: e.VITE_IAP_PUBLIC_KEY,
  };
}

/** Default provider used when nothing is configured. Honest no-op. */
class InertAdProvider implements AdProvider {
  readonly id = 'inert';
  isReady() {
    return false;
  }
  async showRewarded(): Promise<AdResult> {
    return { status: 'not_configured' };
  }
  async showInterstitial(): Promise<AdResult> {
    return { status: 'not_configured' };
  }
}

class InertIapProvider implements IapProvider {
  readonly id = 'inert';
  private catalog: Product[];
  constructor(catalog: Product[]) {
    this.catalog = catalog;
  }
  isReady() {
    return false;
  }
  async getProducts(): Promise<Product[]> {
    return this.catalog;
  }
  async purchase(): Promise<PurchaseResult> {
    return { status: 'unavailable' };
  }
  async restore(): Promise<string[]> {
    return [];
  }
}

export interface MonetizationOptions {
  /** Product catalog (shown in the store even before IAP is wired). */
  catalog?: Product[];
}

export class Monetization {
  private ad: AdProvider;
  private iap: IapProvider;
  readonly env: Env;
  readonly configured: { ads: boolean; iap: boolean };

  constructor(opts: MonetizationOptions = {}) {
    this.env = readEnv();
    this.ad = new InertAdProvider();
    this.iap = new InertIapProvider(opts.catalog || []);
    this.configured = {
      ads: !!(this.env.adsProvider && this.env.adsClient),
      iap: !!(this.env.iapProvider && this.env.iapPublicKey),
    };
    // When real env is present, a wrapper is expected to register a concrete
    // provider via useAdProvider()/useIapProvider(). We surface `configured`
    // so the UI can show a real store instead of a disabled one.
  }

  useAdProvider(p: AdProvider) {
    this.ad = p;
  }
  useIapProvider(p: IapProvider) {
    this.iap = p;
  }

  adsReady() {
    return this.ad.isReady();
  }
  iapReady() {
    return this.iap.isReady();
  }

  showRewarded() {
    return this.ad.showRewarded();
  }
  showInterstitial() {
    return this.ad.showInterstitial();
  }
  getProducts() {
    return this.iap.getProducts();
  }
  purchase(productId: string) {
    return this.iap.purchase(productId);
  }
  restore() {
    return this.iap.restore();
  }
}
